// Step 2 — Customer details, delivery, tier, notes, promo.

import { ArrowLeft, CreditCard, Loader2 } from "lucide-react";
import { ksh } from "@/lib/api";
import { ORDER } from "@/constants/testIds";
import { Field, SegBtn } from "./_shared";

export function OrderStep2Details({
  pricing,
  totalAfterBusiness,
  customerName, setCustomerName,
  customerPhone, setCustomerPhone,
  customerEmail, setCustomerEmail,
  deliveryMethod, setDeliveryMethod,
  deliveryCounty, setDeliveryCounty,
  deliveryTown, setDeliveryTown,
  deliveryAddress, setDeliveryAddress,
  deliveryRecipientName, setDeliveryRecipientName,
  deliveryRecipientPhone, setDeliveryRecipientPhone,
  deliveryInstructions, setDeliveryInstructions,
  tier, setTier,
  notes, setNotes,
  promoInput, setPromoInput,
  appliedPromo, applyPromo, removePromo, applyingPromo,
  creating,
  goBack, submitOrder,
}) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">Your details</h1>
        <p className="text-[#525252]">We&apos;ll use this to contact you when your order is ready.</p>
      </div>
      <div className="bg-white border border-[#E5E5E5] p-6 space-y-4">
        <Field label="Full name" required>
          <input
            data-testid={ORDER.customerName}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="w-full px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
            placeholder="e.g. Wanjiku Kamau"
          />
        </Field>
        <Field label="M-Pesa Phone" required>
          <input
            data-testid={ORDER.customerPhone}
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            className="w-full px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            placeholder="07XXXXXXXX"
          />
        </Field>
        <Field label="Email (for receipt — optional)">
          <input
            data-testid={ORDER.customerEmail}
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            className="w-full px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Pickup or delivery">
          <div className="flex flex-wrap gap-2">
            <SegBtn active={deliveryMethod === "pickup"} onClick={() => setDeliveryMethod("pickup")} testId="delivery-method-pickup">Pickup (free)</SegBtn>
            <SegBtn active={deliveryMethod === "nairobi_delivery"} onClick={() => setDeliveryMethod("nairobi_delivery")} testId="delivery-method-nairobi_delivery">Nairobi delivery</SegBtn>
            <SegBtn active={deliveryMethod === "countrywide"} onClick={() => setDeliveryMethod("countrywide")} testId="delivery-method-countrywide">Countrywide courier</SegBtn>
          </div>
        </Field>
        {deliveryMethod !== "pickup" && (
          <div className="border border-[#E5E5E5] bg-[#F5F5F5] p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">
              § {deliveryMethod === "countrywide" ? "Courier delivery details" : "Nairobi delivery details"}
            </div>
            {deliveryMethod === "countrywide" && (
              <Field label="County" required>
                <input
                  data-testid="delivery-county"
                  value={deliveryCounty}
                  onChange={(e) => setDeliveryCounty(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
                  placeholder="e.g. Mombasa, Kisumu, Nakuru"
                />
              </Field>
            )}
            <Field label="Town / suburb" required>
              <input
                data-testid="delivery-town"
                value={deliveryTown}
                onChange={(e) => setDeliveryTown(e.target.value)}
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
                placeholder="e.g. Westlands"
              />
            </Field>
            <Field label="Address / building / street" required>
              <textarea
                data-testid="delivery-address"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
                placeholder="e.g. ABC Plaza, 3rd floor, Waiyaki Way"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Recipient name (if different)">
                <input
                  data-testid="delivery-recipient-name"
                  value={deliveryRecipientName}
                  onChange={(e) => setDeliveryRecipientName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
                  placeholder="Leave blank to use your name"
                />
              </Field>
              <Field label="Recipient phone (if different)">
                <input
                  data-testid="delivery-recipient-phone"
                  value={deliveryRecipientPhone}
                  onChange={(e) => setDeliveryRecipientPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                  placeholder="07XXXXXXXX"
                />
              </Field>
            </div>
            <Field label="Delivery instructions (for the courier)">
              <textarea
                data-testid="delivery-instructions"
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
                placeholder="Gate code, landmark, preferred time, etc."
              />
            </Field>
          </div>
        )}

        <Field label="Priority Tier">
          <div className="grid sm:grid-cols-3 gap-2">
            {Object.entries(pricing.tiers || {}).map(([key, t]) => {
              const active = tier === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTier(key)}
                  data-testid={`tier-${key}`}
                  className={`text-left p-3 border-2 transition-colors ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
                >
                  <div className="font-display font-black text-xl">{t.label || key}</div>
                  <div className="mono text-xs opacity-80">{t.multiplier === 1 ? "No surcharge" : `+${Math.round((Number(t.multiplier) - 1) * 100)}% surcharge`}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] mt-1 opacity-70">{t.turnaround || ""}</div>
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Order notes (optional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none" placeholder="Special instructions" />
        </Field>
        <Field label="Promo code (optional)">
          {appliedPromo ? (
            <div className="flex items-center justify-between px-3 py-3 border-2 border-[#059669] bg-[#059669]/10" data-testid="promo-applied">
              <div>
                <span className="mono font-bold text-[#059669]">{appliedPromo.code}</span>
                <span className="text-xs text-[#525252] ml-2">-KES {appliedPromo.discount_amount}</span>
              </div>
              <button type="button" onClick={removePromo} data-testid="promo-remove" className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#DC2626] hover:underline">Remove</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="e.g. SAVE10"
                data-testid="promo-input"
                className="flex-1 px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
              />
              <button type="button" onClick={applyPromo} disabled={!promoInput.trim() || applyingPromo} data-testid="promo-apply" className="px-4 py-3 border-2 border-[#0A0A0A] text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50">
                {applyingPromo ? "…" : "Apply"}
              </button>
            </div>
          )}
        </Field>
      </div>
      <div className="flex gap-3">
        <button onClick={goBack} data-testid="back-to-upload" className="px-6 py-4 text-sm uppercase tracking-[0.2em] font-bold border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={submitOrder}
          disabled={creating}
          className="flex-1 bg-[#059669] text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="confirm-order-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          Create order · {ksh(totalAfterBusiness)}
        </button>
      </div>
    </div>
  );
}
